import React, { useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/Button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';

import Layout from '../../Layout/Layout';
import GridPreview from './components/GridPreview';
import MenuImportDialog from './components/MenuImportDialog';
import DishImageDialog from './components/DishImageDialog';
import WallPreviewRenderer from './preview/WallPreviewRenderer';
import { buildTemplatePreviewPayload } from './preview/buildTemplatePreviewPayload';
import { useCustomToast } from '../../Contexts/ToastContext';

import { useCreateMenu } from '../../../queries/signageMenus';
import { useCreateTemplate, useGetThemes } from '../../../queries/signageTemplates';
import { useCreatePlaylist } from '../../../queries/wallPlaylistLibrary';
import { useCreateAssignment } from '../../../queries/wallAssignments';
import { useGetVideoWalls, useGetWallMediaLibrary } from '../../../queries/videoWall';
import { useGetWallGroups } from '../../../queries/wallGroups';
import axiosInstance from '../../../config/Axios';

/**
 * Menu Builder — the unified restaurant flow (replaces the old 4-page hop of
 * Menus → Templates → Library → Assign). One continuous wizard that, on finish,
 * orchestrates the EXISTING create endpoints in order:
 *   POST /menus {name,currency,sections}            → menu
 *   POST /signage-templates {…,screen_assignments,settings} → template
 *   POST /playlists {name,items:[{template}]}        → library playlist
 *   POST /assignments {wall_id|group_id, playlist_id}
 * No backend changes. Sections get server ids only after the menu POST, so the
 * editor assigns by local key and we remap to real ids at submit (order is kept).
 * Reuses GridPreview + WallPreviewRenderer for a live, WYSIWYG preview throughout.
 */

const CURRENCIES = ['SAR', 'AED', 'KWD', 'BHD', 'QAR', 'OMR', 'JOD', 'EGP', 'IQD', 'LBP', 'USD', 'EUR'];
const LAYOUTS = [
  { key: '1x1', label: '1 × 1', rows: 1, cols: 1 },
  { key: '1x2', label: '1 × 2', rows: 1, cols: 2 },
  { key: '2x1', label: '2 × 1', rows: 2, cols: 1 },
  { key: '2x2', label: '2 × 2', rows: 2, cols: 2 },
  { key: '1x3', label: '1 × 3', rows: 1, cols: 3 },
  { key: '3x1', label: '3 × 1', rows: 3, cols: 1 },
];

let KEY = 1;
const nk = (p) => `${p}_${KEY++}`;
const newItem = () => ({ _key: nk('it'), name_ar: '', name_en: '', price: '', photo_media_id: null });
const newSection = () => ({ _key: nk('sec'), name_ar: '', name_en: '', items: [newItem()] });

export default function MenuBuilder() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useCustomToast();

  const [step, setStep] = useState(1);
  const TOTAL = 4;
  const [done, setDone] = useState(null); // null | 'library' (success screen)

  // Step 1
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState('SAR');
  const [orientation, setOrientation] = useState('landscape');
  // Step 2
  const [sections, setSections] = useState([newSection()]);
  const [importOpen, setImportOpen] = useState(false);
  // Step 3
  const { data: themeData } = useGetThemes();
  const themes = themeData?.themes ?? [];
  const defaultZones = useMemo(() => themeData?.defaultZones ?? {}, [themeData]);
  const defaultZonesPortrait = useMemo(() => themeData?.defaultZonesPortrait ?? themeData?.defaultZones ?? {}, [themeData]);
  const [themeKey, setThemeKey] = useState('noir_gold');
  const [rows, setRows] = useState(1);
  const [cols, setCols] = useState(1);
  const [assignments, setAssignments] = useState({}); // { pos: [section _key] }
  const [cellPos, setCellPos] = useState(null);
  // Auto-rotate when any screen holds more than one section (the player shows one
  // section at a time; rotation cycles them). No manual toggle needed in the wizard.
  const rotationMode = useMemo(
    () => (Object.values(assignments).some((a) => a.length > 1) ? 'rotate' : 'none'),
    [assignments],
  );
  // Step 4
  const { data: walls = [] } = useGetVideoWalls();
  const { data: groups = [] } = useGetWallGroups();
  const [target, setTarget] = useState(null); // { kind:'wall'|'group', id, name, orientation }
  // media (dish photos + preview)
  const { data: mediaRaw } = useGetWallMediaLibrary(true);
  const mediaList = useMemo(() => {
    const arr = mediaRaw?.data ?? mediaRaw ?? [];
    return Array.isArray(arr) ? arr : [];
  }, [mediaRaw]);
  const resolvePhoto = useMemo(() => {
    const m = {}; mediaList.forEach((x) => { m[x.id] = x.path || x.thumb || null; });
    return (id) => m[id] || null;
  }, [mediaList]);
  const [photoPicker, setPhotoPicker] = useState(null); // { sk, ik }
  const [aiPicker, setAiPicker] = useState(null); // { sk, ik } for the AI dish dialog
  const qc = useQueryClient();

  const create = {
    menu: useCreateMenu(),
    template: useCreateTemplate(),
    playlist: useCreatePlaylist(),
    assignment: useCreateAssignment(),
  };
  const [busy, setBusy] = useState(null); // null | 'play' | 'library' | 'schedule'

  /* ── section/item mutations ── */
  const patchSection = (sk, p) => setSections((a) => a.map((s) => (s._key === sk ? { ...s, ...p } : s)));
  const patchItem = (sk, ik, p) => setSections((a) => a.map((s) => (
    s._key !== sk ? s : { ...s, items: s.items.map((it) => (it._key === ik ? { ...it, ...p } : it)) })));
  const addSection = () => setSections((a) => [...a, newSection()]);
  const removeSection = (sk) => setSections((a) => a.filter((s) => s._key !== sk));
  const addItem = (sk) => setSections((a) => a.map((s) => (s._key === sk ? { ...s, items: [...s.items, newItem()] } : s)));
  const removeItem = (sk, ik) => setSections((a) => a.map((s) => (
    s._key !== sk ? s : { ...s, items: s.items.filter((it) => it._key !== ik) })));

  const sectionName = (s) => s.name_en || s.name_ar || t('video_wall.templates.untitled_section');

  // AI dish-photo dialog (opened from the photo picker, acts on aiPicker {sk,ik}).
  const aiItem = useMemo(() => {
    if (!aiPicker) return null;
    const s = sections.find((x) => x._key === aiPicker.sk);
    return s?.items.find((it) => it._key === aiPicker.ik) || null;
  }, [aiPicker, sections]);
  const onAiSaved = (media) => {
    if (aiPicker) patchItem(aiPicker.sk, aiPicker.ik, { photo_media_id: media.media_id });
    qc.invalidateQueries('videoWallMediaLibrary');
    setAiPicker(null);
  };

  /* ── AI Menu OCR: turn a Gemini draft into wizard sections (replace if the
     menu is still untouched, otherwise append so manual work isn't lost). ── */
  const draftToSections = (draft) => (draft.sections || []).map((s) => ({
    _key: nk('sec'), name_ar: s.name_ar || '', name_en: s.name_en || '',
    items: (s.items?.length ? s.items : [{}]).map((it) => ({
      _key: nk('it'), name_ar: it.name_ar || '', name_en: it.name_en || '',
      price: it.price || '', photo_media_id: null,
    })),
  }));
  const isPristine = (arr) => arr.length === 1 && !arr[0].name_ar.trim() && !arr[0].name_en.trim()
    && arr[0].items.every((it) => !it.name_ar.trim() && !it.name_en.trim() && !it.price.trim());
  const applyDraft = (draft) => {
    const incoming = draftToSections(draft);
    if (!incoming.length) return;
    setSections((cur) => (isPristine(cur) ? incoming : [...cur, ...incoming]));
    if (draft.currency && CURRENCIES.includes(draft.currency.toUpperCase())) {
      setCurrency(draft.currency.toUpperCase());
    }
    if (!name.trim()) setName(t('video_wall.menu_ocr.default_menu_name'));
  };

  /* ── assignment ── */
  const allPositions = useMemo(() => Array.from({ length: rows * cols }, (_, i) => i + 1), [rows, cols]);
  const setLayout = (l) => {
    setRows(l.rows); setCols(l.cols);
    const max = l.rows * l.cols;
    setAssignments((a) => Object.fromEntries(Object.entries(a).filter(([k]) => Number(k) <= max)));
  };
  const toggleSectionOnCell = (pos, sk) => setAssignments((a) => {
    const cur = a[pos] || [];
    const next = cur.includes(sk) ? cur.filter((x) => x !== sk) : [...cur, sk];
    const copy = { ...a };
    if (next.length) copy[pos] = next; else delete copy[pos];
    return copy;
  });
  const addSectionToAll = (sk) => setAssignments((a) => {
    const copy = { ...a };
    allPositions.forEach((pos) => { const cur = copy[pos] || []; if (!cur.includes(sk)) copy[pos] = [...cur, sk]; });
    return copy;
  });
  const cellLabel = (pos) => (assignments[pos] || [])
    .map((sk) => sectionName(sections.find((s) => s._key === sk) || {})).join(', ');

  /* ── live preview (uses _key as the section id; same shape the player consumes) ── */
  const theme = themes.find((th) => th.key === themeKey);
  const grad = theme?.gradient || ['#1A1A1A', '#000000'];
  const previewSections = useMemo(() => sections.map((s) => ({
    id: s._key,
    name_ar: s.name_ar, name_en: s.name_en,
    items: s.items.map((it) => ({
      id: it._key, name_ar: it.name_ar, name_en: it.name_en, price: it.price, currency,
      photo_url: it.photo_media_id ? resolvePhoto(it.photo_media_id) : null,
    })),
  })), [sections, currency, resolvePhoto]);
  const previewTemplate = useMemo(() => buildTemplatePreviewPayload({
    instance: {
      type: 'menu', theme_key: themeKey,
      screen_assignments: Object.keys(assignments).length ? assignments : null,
      settings: { rotation_mode: rotationMode, rotation_interval_ms: 8000, logo_url: null },
    },
    sections: previewSections,
    theme: theme || {},
    defaultZones: orientation === 'portrait' ? defaultZonesPortrait : defaultZones,
    currency,
    resolvePhoto,
  }), [themeKey, assignments, rotationMode, previewSections, theme, orientation, defaultZones, defaultZonesPortrait, currency, resolvePhoto]);

  /* ── per-step validation (steps 1-3 gate Next; step 4 is the terminal action step) ── */
  const stepValid = (s) => {
    if (s === 1) return !!name.trim();
    if (s === 2) return sections.length > 0 && sections.every((sec) => (sec.name_ar.trim() || sec.name_en.trim()) && sec.items.length > 0);
    if (s === 3) return Object.keys(assignments).length > 0; // at least one screen assigned
    return true;
  };
  const canNext = stepValid(step);

  /* ── orchestrated create: menu → template → library playlist (NO assignment) ──
     Shared by all three finish actions. Returns the new playlist id (+name). */
  const unwrap = (res) => res?.data?.data ?? res?.data ?? res;
  const createContent = async () => {
    // 1) menu (+ sections/items)
    const menuRes = unwrap(await create.menu.mutateAsync({
      name: name.trim(), currency,
      sections: sections.map((s) => ({
        name_ar: s.name_ar || null, name_en: s.name_en || null,
        items: s.items.map((it) => ({
          name_ar: it.name_ar || null, name_en: it.name_en || null,
          price: it.price || null, photo_media_id: it.photo_media_id || null,
        })),
      })),
    }));
    const menu = menuRes?.menu;
    // map local section _key → server section id (storeSections preserves order)
    const serverSections = menu?.sections || [];
    const keyToId = {};
    sections.forEach((s, i) => { if (serverSections[i]) keyToId[s._key] = serverSections[i].id; });
    const realAssignments = {};
    Object.entries(assignments).forEach(([pos, keys]) => {
      const ids = keys.map((k) => keyToId[k]).filter(Boolean);
      if (ids.length) realAssignments[pos] = ids;
    });

    // 2) template
    const tplRes = unwrap(await create.template.mutateAsync({
      name: name.trim(), type: 'menu', theme_key: themeKey, menu_id: menu.id,
      screen_assignments: Object.keys(realAssignments).length ? realAssignments : null,
      settings: {
        rotation_mode: rotationMode, rotation_interval_ms: 8000, logo_url: null,
        orientation, editor_grid: { rows, cols },
      },
    }));
    const templateId = tplRes?.template?.id;

    // 3) library playlist holding the template item
    const plRes = unwrap(await create.playlist.mutateAsync({
      name: name.trim(),
      items: [{ content_type: 'template', template_instance_id: templateId, duration_seconds: 30 }],
    }));
    return { playlistId: plRes?.playlist?.id };
  };

  // Save & Play Now — assign to the chosen wall/group AND start it immediately.
  const saveAndPlay = async () => {
    if (!target) return;
    setBusy('play');
    try {
      const { playlistId } = await createContent();
      // Assigning an always-on playlist to a group makes it the group default for
      // every member: the backend (PlaylistAssignmentController::store) clears any
      // members' own always-on wall-direct overrides so none shadows the group.
      await create.assignment.mutateAsync(
        target.kind === 'group'
          ? { group_id: target.id, playlist_id: playlistId }
          : { wall_id: target.id, playlist_id: playlistId },
      );
      // Start playing now (assignment alone only sets the default). Group start
      // fans out to every member; each now resolves the group's playlist.
      const startUrl = target.kind === 'group'
        ? `/wall-groups/${target.id}/start`
        : `/video-walls/${target.id}/start`;
      try { await axiosInstance.post(startUrl); } catch { /* surfaced below if needed */ }
      showToast('success', 'Menu Builder', t('video_wall.menu_builder.done_toast'));
      navigate(target.kind === 'group' ? `/apps/video-wall/groups/${target.id}` : `/apps/video-wall/${target.id}`);
    } catch {
      setBusy(null); // individual hooks already toast their own errors
    }
  };

  // Save to Library — create the content, assign nothing; show the success screen.
  const saveToLibrary = async () => {
    setBusy('library');
    try {
      await createContent();
      setDone('library');
    } catch {
      setBusy(null);
    }
  };

  // Save & Schedule — create the content, then go to the visual Scheduler.
  const saveAndSchedule = async () => {
    setBusy('schedule');
    try {
      const { playlistId } = await createContent();
      showToast('success', 'Menu Builder', t('video_wall.menu_builder.saved_toast'));
      navigate(`/apps/video-wall/schedule${playlistId ? `?playlist=${playlistId}` : ''}`);
    } catch {
      setBusy(null);
    }
  };

  const STEP_TITLES = [
    t('video_wall.menu_builder.step_basics'),
    t('video_wall.menu_builder.step_sections'),
    t('video_wall.menu_builder.step_design'),
    t('video_wall.menu_builder.step_finish'),
  ];

  /* ── Success screen after "Save to Library" ── */
  if (done === 'library') {
    return (
      <Layout>
        <div className="pp-card p-5 text-center" style={{ maxWidth: 560, margin: '3rem auto' }}>
          <div style={{ fontSize: '3rem' }}>🎉</div>
          <h4 className="mt-2" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.menu_builder.library_done_title')}</h4>
          <p style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.menu_builder.library_done_hint')}</p>
          <div className="d-flex gap-2 justify-content-center mt-3 flex-wrap">
            <Button label={t('video_wall.menu_builder.go_my_walls')} icon="pi pi-tv"
              onClick={() => navigate('/apps/video-wall/walls')} />
            <Button label={t('video_wall.menu_builder.build_another')} icon="pi pi-plus" outlined
              onClick={() => window.location.reload()} />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header + stepper */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
        <div className="d-flex align-items-center gap-2">
          <Button text icon="pi pi-arrow-left" onClick={() => navigate('/apps/video-wall/walls')} className="p-0" />
          <h4 className="mb-0" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.menu_builder.title')}</h4>
        </div>
      </div>
      <div className="d-flex gap-2 mb-4 flex-wrap">
        {STEP_TITLES.map((title, i) => {
          const n = i + 1;
          const active = n === step;
          const done = n < step;
          return (
            <div key={n} className="d-flex align-items-center gap-2 px-3 py-2" style={{
              borderRadius: 999, fontSize: 13,
              background: active ? 'var(--pp-btn-honey)' : done ? 'color-mix(in srgb, var(--pp-btn-honey) 10%, transparent)' : 'var(--pp-bg-tertiary)',
              color: active ? '#fff' : done ? 'var(--pp-btn-honey-hover)' : 'var(--pp-text-secondary)',
            }}>
              <span style={{ fontWeight: 700 }}>{done ? <i className="pi pi-check" /> : n}</span>
              <span>{title}</span>
            </div>
          );
        })}
      </div>

      <div className="row g-4">
        {/* Main step body */}
        <div className="col-12 col-lg-7">
          <div className="pp-card p-4">
            {step === 1 && (
              <div className="d-flex flex-column gap-3">
                <div>
                  <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>{t('video_wall.menu_builder.menu_name')}</label>
                  <InputText className="w-100" value={name} onChange={(e) => setName(e.target.value)} maxLength={150}
                    placeholder={t('video_wall.menu_builder.menu_name_ph')} />
                </div>
                <div style={{ maxWidth: 220 }}>
                  <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>{t('video_wall.menus.currency')}</label>
                  <Dropdown className="w-100" value={currency} onChange={(e) => setCurrency(e.value)}
                    options={CURRENCIES.map((c) => ({ label: c, value: c }))} />
                </div>
                <div>
                  <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>{t('video_wall.templates.orientation')}</label>
                  <div className="d-flex gap-2">
                    <Button size="small" icon="pi pi-window-maximize" label={t('video_wall.templates.landscape')}
                      outlined={orientation !== 'landscape'} onClick={() => setOrientation('landscape')} />
                    <Button size="small" icon="pi pi-mobile" label={t('video_wall.templates.portrait')}
                      outlined={orientation !== 'portrait'} onClick={() => setOrientation('portrait')} />
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="d-flex flex-column gap-3">
                {/* AI Menu OCR — fill the whole menu from a photo/PDF */}
                <div className="d-flex align-items-center justify-content-between gap-2 p-3 flex-wrap"
                  style={{ background: 'var(--pp-primary-subtle)', borderRadius: 'var(--pp-radius-md)', border: '1px solid var(--pp-primary)' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: 'var(--pp-text-primary)', fontWeight: 600 }}>
                      <i className="pi pi-sparkles me-2" style={{ color: 'var(--pp-primary)' }} />{t('video_wall.menu_ocr.cta_title')}
                    </div>
                    <div className="small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.menu_ocr.cta_hint')}</div>
                  </div>
                  <Button size="small" icon="pi pi-camera" label={t('video_wall.menu_ocr.cta_button')} onClick={() => setImportOpen(true)} />
                </div>
                {sections.map((s) => (
                  <div key={s._key} className="p-3" style={{ background: 'var(--pp-bg-secondary)', border: '1px solid var(--pp-border)', borderRadius: 'var(--pp-radius-md)' }}>
                    <div className="d-flex gap-2 mb-2">
                      <InputText className="flex-grow-1" value={s.name_en} onChange={(e) => patchSection(s._key, { name_en: e.target.value })} placeholder={t('video_wall.menu_builder.section_en')} />
                      <InputText className="flex-grow-1" value={s.name_ar} onChange={(e) => patchSection(s._key, { name_ar: e.target.value })} placeholder={t('video_wall.menu_builder.section_ar')} dir="rtl" />
                      <Button icon="pi pi-trash" text rounded severity="danger" onClick={() => removeSection(s._key)} disabled={sections.length <= 1} />
                    </div>
                    {s.items.map((it) => (
                      <div key={it._key} className="d-flex gap-2 mb-2 align-items-center">
                        <InputText className="flex-grow-1" value={it.name_en} onChange={(e) => patchItem(s._key, it._key, { name_en: e.target.value })} placeholder={t('video_wall.menu_builder.item_en')} />
                        <InputText className="flex-grow-1" value={it.name_ar} onChange={(e) => patchItem(s._key, it._key, { name_ar: e.target.value })} placeholder={t('video_wall.menu_builder.item_ar')} dir="rtl" />
                        <InputText style={{ width: 80 }} value={it.price} onChange={(e) => patchItem(s._key, it._key, { price: e.target.value })} placeholder={t('video_wall.menu_builder.price')} />
                        <Button icon={it.photo_media_id ? 'pi pi-image' : 'pi pi-images'} text rounded
                          severity={it.photo_media_id ? 'success' : undefined}
                          title={t('video_wall.menu_builder.photo')} onClick={() => setPhotoPicker({ sk: s._key, ik: it._key })} />
                        <Button icon="pi pi-times" text rounded severity="danger" onClick={() => removeItem(s._key, it._key)} disabled={s.items.length <= 1} />
                      </div>
                    ))}
                    <Button size="small" text icon="pi pi-plus" label={t('video_wall.menu_builder.add_item')} onClick={() => addItem(s._key)} />
                  </div>
                ))}
                <Button text icon="pi pi-plus" label={t('video_wall.menu_builder.add_section')} onClick={addSection} className="align-self-start" />
              </div>
            )}

            {step === 3 && (
              <div className="d-flex flex-column gap-3">
                {/* theme */}
                <div>
                  <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>{t('video_wall.templates.theme')}</label>
                  <div className="d-flex flex-wrap gap-2">
                    {themes.map((th) => {
                      const g = th.gradient || ['#1A1A1A', '#000000'];
                      const selected = th.key === themeKey;
                      return (
                        <button key={th.key} type="button" onClick={() => setThemeKey(th.key)} title={th.label}
                          className={`pp-swatch${selected ? ' is-selected' : ''}`}
                          style={{ width: 84, padding: 3 }}>
                          <div style={{ height: 34, borderRadius: 7, background: `linear-gradient(to bottom, ${g[0]}, ${g[g.length - 1]})` }} />
                          <div className="small text-truncate mt-1" style={{ color: selected ? 'var(--pp-text-primary)' : 'var(--pp-text-secondary)' }}>{th.label}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {/* layout */}
                <div>
                  <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>{t('video_wall.templates.layout')}</label>
                  <div className="d-flex flex-wrap gap-2">
                    {LAYOUTS.map((l) => (
                      <Button key={l.key} label={l.label} size="small" outlined={!(l.rows === rows && l.cols === cols)} onClick={() => setLayout(l)} />
                    ))}
                  </div>
                </div>
                {/* assignment grid */}
                <div style={{ background: `linear-gradient(to bottom, ${grad[0]}, ${grad[grad.length - 1]})`, padding: 16, borderRadius: 12 }}>
                  <GridPreview rows={rows} columns={cols} orientation={orientation} maxWidth={380}
                    renderCell={(r, c, pos) => {
                      const label = cellLabel(pos);
                      return (
                        <div onClick={() => setCellPos(pos)} title={t('video_wall.templates.assign_cell', { n: pos })}
                          style={{ width: '100%', height: '100%', cursor: 'pointer', padding: 4, textAlign: 'center',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11,
                            color: label ? '#fff' : 'rgba(255,255,255,0.6)' }}>
                          {label || `${t('video_wall.templates.cell')} ${pos}`}
                        </div>
                      );
                    }}
                    cellStyle={() => ({ background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.25)' })} />
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Dropdown value={null} onChange={(e) => addSectionToAll(e.value)} options={sections.map((s) => ({ label: sectionName(s), value: s._key }))}
                    placeholder={t('video_wall.templates.add_to_all')} style={{ minWidth: 200 }} />
                  <span className="small" style={{ color: 'var(--pp-text-secondary)' }}>
                    <i className="pi pi-info-circle me-1" />{t('video_wall.templates.fallback_hint')}
                  </span>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="d-flex flex-column gap-3">
                {/* Review summary */}
                <div className="d-flex flex-column gap-1">
                  <Row k={t('video_wall.menu_builder.menu_name')} v={name} />
                  <Row k={t('video_wall.menus.currency')} v={currency} />
                  <Row k={t('video_wall.templates.orientation')} v={t(`video_wall.templates.${orientation}`)} />
                  <Row k={t('video_wall.menu_builder.step_sections')} v={`${sections.length}`} />
                  <Row k={t('video_wall.templates.theme')} v={theme?.label || themeKey} />
                  <Row k={t('video_wall.templates.layout')} v={`${rows} × ${cols}`} />
                </div>

                {/* Optional target — only needed for "Save & Play Now" */}
                <div>
                  <div className="small fw-bold mb-1" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.menu_builder.play_now_pick')}</div>
                  <p className="small mb-2" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.menu_builder.play_now_hint')}</p>
                  <div className="d-flex flex-column gap-2" style={{ maxHeight: 230, overflowY: 'auto' }}>
                    {groups.map((g) => (
                      <TargetRow key={g.id} active={target?.kind === 'group' && target.id === g.id}
                        icon="pi pi-th-large" name={g.name} sub={g.orientation}
                        onClick={() => setTarget({ kind: 'group', id: g.id, name: g.name, orientation: g.orientation })} />
                    ))}
                    {walls.map((w) => (
                      <TargetRow key={w.id} active={target?.kind === 'wall' && target.id === w.id}
                        icon="pi pi-desktop" name={w.name} sub={`${w.rows}×${w.columns} · ${w.orientation}`}
                        onClick={() => setTarget({ kind: 'wall', id: w.id, name: w.name, orientation: w.orientation })} />
                    ))}
                  </div>
                  {target && target.orientation && target.orientation !== orientation && (
                    <p className="small mt-2 mb-0" style={{ color: 'var(--pp-warning, #f59e0b)' }}>
                      <i className="pi pi-exclamation-triangle me-1" />{t('video_wall.menu_builder.orientation_warn', { target: t(`video_wall.templates.${target.orientation}`) })}
                    </p>
                  )}
                </div>

                {/* Three finish actions */}
                <div className="d-grid gap-2 pt-2" style={{ borderTop: '1px solid var(--pp-border)' }}>
                  <Button label={t('video_wall.menu_builder.save_play')} icon="pi pi-play" severity="success" size="large"
                    disabled={!target || !!busy} loading={busy === 'play'} onClick={saveAndPlay} />
                  <div className="d-flex gap-2">
                    <Button label={t('video_wall.menu_builder.save_library')} icon="pi pi-bookmark" outlined className="flex-fill"
                      disabled={!!busy} loading={busy === 'library'} onClick={saveToLibrary} />
                    <Button label={t('video_wall.menu_builder.save_schedule')} icon="pi pi-calendar-plus" outlined className="flex-fill"
                      disabled={!!busy} loading={busy === 'schedule'} onClick={saveAndSchedule} />
                  </div>
                  <p className="small mb-0 mt-1" style={{ color: 'var(--pp-text-secondary)' }}>
                    <i className="pi pi-info-circle me-1" />{t('video_wall.menu_builder.actions_hint')}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Nav — Next only on steps 1-3; step 4 finishes via the action buttons above */}
          <div className="d-flex justify-content-between mt-3">
            <Button label={t('video_wall.menu_builder.back')} icon="pi pi-arrow-left" outlined
              disabled={step === 1 || !!busy} onClick={() => setStep((s) => s - 1)} />
            {step < TOTAL && (
              <Button label={t('video_wall.menu_builder.next')} icon="pi pi-arrow-right" iconPos="right"
                disabled={!canNext} onClick={() => setStep((s) => s + 1)} />
            )}
          </div>
        </div>

        {/* Live preview */}
        <div className="col-12 col-lg-5">
          <div className="pp-card p-3">
            <h6 className="mb-2" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.menu_builder.live_preview')}</h6>
            <WallPreviewRenderer template={previewTemplate} grid={{ rows, cols, orientation }} maxWidth={420} />
          </div>
        </div>
      </div>

      {/* per-cell section picker */}
      <Dialog visible={cellPos != null} onHide={() => setCellPos(null)}
        header={t('video_wall.templates.assign_cell', { n: cellPos })} style={{ width: 'min(94vw, 420px)' }}>
        <div className="d-flex flex-column gap-2 pt-2">
          {sections.map((s) => {
            const checked = (assignments[cellPos] || []).includes(s._key);
            return (
              <label key={s._key} className="d-flex align-items-center gap-2 p-2" style={{ cursor: 'pointer', borderRadius: 8, background: checked ? 'var(--pp-bg-tertiary)' : 'transparent' }}>
                <Checkbox checked={checked} onChange={() => toggleSectionOnCell(cellPos, s._key)} />
                <span style={{ color: 'var(--pp-text-primary)' }}>{sectionName(s)}</span>
              </label>
            );
          })}
        </div>
      </Dialog>

      {/* AI Menu OCR import */}
      <MenuImportDialog visible={importOpen} onHide={() => setImportOpen(false)} onImported={applyDraft} />

      {/* AI dish-photo generate/enhance */}
      <DishImageDialog visible={!!aiPicker} onHide={() => setAiPicker(null)}
        initialDishName={aiItem?.name_en || aiItem?.name_ar || ''} onSaved={onAiSaved} />

      {/* dish photo picker */}
      <Dialog visible={!!photoPicker} onHide={() => setPhotoPicker(null)} header={t('video_wall.menu_builder.photo')} style={{ width: 'min(95vw, 560px)' }}>
        <Button className="w-100 mb-3" icon="pi pi-sparkles" label={t('video_wall.dish_ai.cta_button')}
          onClick={() => { setAiPicker(photoPicker); setPhotoPicker(null); }} />
        <div className="row g-2">
          {mediaList.filter((m) => m.type === 'IMAGE').map((m) => (
            <div key={m.id} className="col-4">
              <button type="button" className="p-0 border-0 w-100" style={{ background: 'transparent', cursor: 'pointer' }}
                onClick={() => { patchItem(photoPicker.sk, photoPicker.ik, { photo_media_id: m.id }); setPhotoPicker(null); }}>
                <img src={m.thumb || m.path} alt={m.file_name} className="w-100" style={{ height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--pp-border)' }} />
              </button>
            </div>
          ))}
        </div>
      </Dialog>
    </Layout>
  );
}

function TargetRow({ active, icon, name, sub, onClick }) {
  return (
    <div onClick={onClick} className="d-flex align-items-center gap-2 p-2" style={{
      cursor: 'pointer', borderRadius: 'var(--pp-radius-md)',
      border: `1px solid ${active ? 'var(--pp-btn-honey)' : 'var(--pp-border)'}`,
      background: active ? 'color-mix(in srgb, var(--pp-btn-honey) 10%, transparent)' : 'var(--pp-bg-elevated)' }}>
      <i className={icon} style={{ color: 'var(--pp-text-secondary)' }} />
      <span style={{ color: 'var(--pp-text-primary)', fontWeight: 600 }}>{name}</span>
      {sub && <span className="small ms-auto" style={{ color: 'var(--pp-text-secondary)' }}>{sub}</span>}
      {active && <i className="pi pi-check-circle" style={{ color: 'var(--pp-btn-honey)' }} />}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="d-flex justify-content-between" style={{ borderBottom: '1px solid var(--pp-border)', padding: '6px 0' }}>
      <span className="small" style={{ color: 'var(--pp-text-secondary)' }}>{k}</span>
      <span className="small" style={{ color: 'var(--pp-text-primary)', fontWeight: 600 }}>{v}</span>
    </div>
  );
}
